import type { DisplayConfig, DynamicServicePricingConfig } from './pricebook';
import type { PricingTreatment } from '@/lib/sales/service-rollup';
import type { TaxGroupItem } from './tax';

/**
 * A dynamic-service configuration as it exists on a DOCUMENT line.
 *
 * Everything the catalog's `DynamicServicePricingConfig` says, plus the one
 * thing only a document can have: the flat pricing this line was carrying when
 * the operator switched it to Dynamic.
 *
 * Separate from the catalog type because a PRICEBOOK ITEM has no dormant flat
 * price — "before Dynamic" is something that happened on one document, to one
 * line. Declaring it here means a catalog payload cannot express the parked
 * triple, so it cannot carry it by accident; `buildPricebookItemFromLine` still
 * strips it explicitly, because a type is not a runtime filter and that function
 * spreads the whole configuration across the one boundary where a LINE becomes
 * an ITEM.
 */
export interface DocumentDynamicPricingConfig extends DynamicServicePricingConfig {
  /**
   * THE FLAT PRICING THIS LINE WAS CARRYING when it was switched to Dynamic,
   * kept so switching back hands it straight over.
   *
   * SPEC-line-item-model, "Flat vs Dynamic": toggling between the modes "must
   * never overwrite the typed price: the operator's number is simply dormant
   * while the service is dynamic, and it has to still be there when they switch
   * back." Materializing spends every field that described the flat price —
   * `basePrice` becomes the calculated price, `manualPriceOverride` is cleared
   * so it cannot outrank the formula, and `memberPrice` is restated against the
   * new price so the member RATE survives rather than the stale numerator.
   *
   * Parked ON THE CONFIGURATION, and therefore persisted: a price kept anywhere
   * the save does not reach is a price that is gone by the next reload, which is
   * what the spec forbids. The customer never sees it — `dynamicPricingConfig`
   * is named in `PUBLIC_ESTIMATE_LINE_FIELDS` as deliberately withheld.
   */
  flatPricingBeforeDynamic?: {
    /** The pricebook's price — the member rate's denominator. */
    basePrice: number;
    /** The operator's price for THIS document, or `null` where there was none. */
    manualPriceOverride: number | null;
    /**
     * The member rate's NUMERATOR, measured against the `basePrice` above.
     *
     * Parked with it because the two are only ever read together, as the ratio
     * `amount x memberPrice / basePrice`. Coming back restores the pair the
     * pricebook stated rather than a figure rescaled twice.
     */
    memberPrice?: number | null;
  };
}

// ─── Estimates (operations microservice) ─────────────────────────────────────

export interface Tag {
  id: string;
  tenantId: string;
  scope: string;
  name: string;
  color?: string | null;
  createdAt: string;
  updatedAt: string;
}

export type TagScope =
  | 'contact'
  | 'customer'
  | 'estimate'
  | 'job'
  | 'membership'
  | 'pricebook_item'
  | 'visit';

export interface CreateTagDto {
  scope: TagScope;
  name: string;
  color?: string;
}

export interface UpdateTagDto {
  name?: string;
  color?: string;
}

// Customer approval method (spec v1.2 §Chunk 4)
export type CustomerApprovalMethod = 'SIGNATURE' | 'ONLINE' | 'VERBAL' | 'EMAIL';

// Booking type (spec v1.2 §3.8)
export type EstimateBookingType = 'ONE_OFF' | 'RECURRING';

export type EstimateFulfillmentMode = 'CURRENT_JOB' | 'FOLLOW_UP_JOB' | 'UNDECIDED';

export type EstimateRecurringFrequency =
  | 'WEEKLY'
  | 'BIWEEKLY'
  | 'MONTHLY'
  | 'QUARTERLY'
  | 'ANNUALLY'
  | 'CUSTOM';

export type EstimateRecurringIntervalUnit = 'DAYS' | 'WEEKS' | 'MONTHS' | 'YEARS';

export type EstimateRecurringEndType = 'INDEFINITE' | 'AFTER_COUNT' | 'ON_DATE';

export interface EstimateRecurringConfig {
  frequency: EstimateRecurringFrequency;
  interval?: number;
  intervalUnit?: EstimateRecurringIntervalUnit;
  dayOfWeek?: string;
  // NOT-240: selected weekdays (0=Sun…6=Sat) for WEEKLY/BIWEEKLY/custom-by-weeks.
  customDays?: number[];
  visitStartTime?: string;
  visitEndTime?: string | null;
  visitPatterns?: Array<{
    id: string;
    kind?: 'DAILY_TIME' | 'WEEKDAY' | 'MONTH_DAY' | 'MONTH_WEEKDAY' | 'YEAR_DATE';
    dayOfWeek?: number;
    dayOfMonth?: number;
    weekOfMonth?: number;
    month?: number;
    startTime: string;
    endTime: string;
  }>;
  visitGranularity?: 'TIMED' | 'DAY_FLEXIBLE';
  holidayPolicy?: 'SKIP' | 'NEXT_BUSINESS_DAY' | 'PROCEED';
  seasonStart?: string;
  seasonEnd?: string;
  blackoutDates?: string[];
  startDate: string;
  endType?: EstimateRecurringEndType;
  endAfterCount?: number;
  endOnDate?: string;
  monthMode?: 'dayOfMonth' | 'dayOfWeek';
  monthDay?: number;
  monthDayOfWeek?: number;
  monthDayOfWeekOccurrence?: number;
  visitsGenerated?: number;
  nextVisitDate?: string;
  billingMode?: 'PER_VISIT' | 'SUBSCRIPTION' | 'PREPAID';
  baseAmount?: number;
  billingCadence?: {
    freq: 'MONTHLY' | 'QUARTERLY' | 'ANNUALLY';
    interval: number;
  };
  billingAnchorDay?: number;
  termMonths?: number;
  prorationPolicy?: 'DAILY' | 'FULL_MONTH' | 'NONE';
  currency?: string;
  minimumVisits?: number;
  overageRate?: number;
  escalation?: {
    type: 'NONE' | 'FIXED_PERCENT' | 'FIXED_AMOUNT' | 'CPI_INDEXED' | 'SCHEDULED_STEPS';
    value?: number;
    cpiIndex?: string;
    ruleId?: string;
    steps?: Array<{ year: number; amount: number }>;
  };
  autoRenew?: boolean;
  pauseBillingPolicy?: 'CONTINUE' | 'PRORATE_CREDIT' | 'SUSPEND';
  cancellationFeePolicy?: 'none' | 'flat' | 'minimum_term_remaining';
  minimumTermMonths?: number;
  cancellationFee?: number;
}

export type EstimateStatus =
  | 'DRAFT'
  | 'REVIEW'
  | 'SENT'
  | 'VIEWED'
  | 'FOLLOW_UP'
  | 'APPROVED'
  | 'DECLINED'
  | 'EXPIRED'
  | 'REVISED'
  | 'CONVERTED';

/**
 * Platform origin of the estimate — how/where it was created. Derived by the
 * backend (`deriveEstimateSource`) at creation and immutable afterwards; the
 * FE cannot set it. Distinct from `metadata.source`, which is the
 * user-selected sales attribution.
 */
export type EstimateSource = 'INTERNAL' | 'AI' | 'LEAD' | 'PROPOSAL' | 'JOB' | 'REPORT';

/**
 * Attribution keys the UI reads out of an estimate's `metadata` blob. The blob
 * also carries backend-owned keys (proposal back-references, AI confidence,
 * captured signature) that consumers round-trip untouched, so the shape stays
 * open via the index signature rather than closing over these keys.
 */
export interface EstimateAttributionMetadata {
  /** Sales attribution chosen by the user (MANUAL, INBOUND_LEAD, …). */
  source?: string | null;
  /** Originating role: OFFICE, TECH, AI or CUSTOMER. */
  createdByRole?: string | null;
  techId?: string | null;
  salespersonId?: string | null;
  leadId?: string | null;
  leadSource?: string | null;
  leadSourceId?: string | null;
}

export type EstimateMetadata = EstimateAttributionMetadata & Record<string, unknown>;

/**
 * NOT-625 — the "join and save today" offer frozen onto the estimate at
 * create/reprice time (`membershipPlanSnapshot.offer`). Same shape as the
 * form's live `MembershipOffer` preview (useMembershipOfferPreview), but
 * persisted server-side so the recipient's public shared-link view and the
 * PDF — which cannot reach the auth-only plan/equipment endpoints — show the
 * exact offer the rep saw.
 */
export interface EstimateMembershipOffer {
  setupFee?: number;
  planId: string;
  planName: string;
  /** What the document costs today (no membership). */
  nonMemberGrandTotal: number;
  /** What it would cost as a member of this plan. */
  memberGrandTotal: number;
  /** nonMemberGrandTotal − memberGrandTotal (> 0). */
  savings: number;
  /** The plan's own price for this home (the cost of joining). */
  planPrice: number | null;
  planPriceInterval: string | null;
  /** Number of interval units covered by the quoted plan price. */
  planPriceIntervalValue?: number | null;
  /** Billing option initially shown to the customer. */
  billingOptionId?: string | null;
  billingOptionLabel?: string | null;
  /**
   * Frozen choices the estimate author explicitly left to the customer.
   * Absent means the author already chose the one quoted option.
   */
  billingOptions?: EstimateMembershipBillingOption[];
  /** Integrity fingerprint over the frozen plan's commercial and entitlement terms. */
  planTermsFingerprint?: string;
  lineAdjustments?: EstimateMembershipOfferLineAdjustment[];
  selectionPricing?: {
    documentDiscount: { type: 'PERCENT' | 'FLAT'; value: number };
    taxGroup: {
      id: string;
      name: string;
      combinedRate: number;
      resolvedFrom: string;
      items: Array<
        Pick<TaxGroupItem, 'sortOrder' | 'isCompound'> & {
          taxCode: Pick<TaxGroupItem['taxCode'], 'rate' | 'rateType'> & {
            name?: string | null;
            appliesTo?: string | string[] | null;
            jurisdiction?: string | null;
            jurisdictionType?: string | null;
          };
        }
      >;
    } | null;
    lines: Array<{
      lineIndex: number;
      lineItemId: string;
      lineKey?: string | null;
      parentLineKey?: string | null;
      componentDisplayMode?: string | null;
      type: string;
      taxable: boolean;
      taxLineType?: string | null;
      taxCategory?: string | null;
      isSelected: boolean;
      isOptional: boolean;
      subtotal: number;
      originalNet: number;
      memberNet: number;
    }>;
  };
}

export interface EstimateMembershipBillingOption {
  id: string;
  label: string;
  price: number;
  intervalValue: number;
  intervalUnit: string;
}

export interface EstimateMembershipOfferLineAdjustment {
  lineIndex: number;
  lineKey: string | null;
  lineItemId: string;
  originalSubtotal: number;
  memberSubtotal: number;
  originalTax: number;
  memberTax: number;
  originalTotal: number;
  memberTotal: number;
  savings: number;
}

export interface EstimateCustomer {
  name: string;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
  primaryContactName?: string | null;
  primaryContactTitle?: string | null;
}

/**
 * NOT-590 — how the CUSTOMER sees a service's components.
 *
 * Presentation only. The components are separate priced lines either way; this
 * decides whether the customer sees them broken out or folded into one amount.
 * The pricebook item supplies the default and the estimate line may override it.
 */
export type ComponentDisplayMode = 'ITEMIZED' | 'ROLLED_UP';

export interface EstimateLineItem {
  /**
   * The editor's own row identity, carried through the save so line
   * attachments staged against a row still find it once the line is stored.
   * Minted client-side, which is why it is optional: a line that arrived from
   * anywhere but the editor has none.
   */
  rowId?: string;
  lineItemId: string;
  itemVersion: number;
  name: string;
  code: string;
  type: string;
  unit: string;
  description?: string;
  basePrice: number;
  laborRate: number;
  costBasis: number;
  markupPercent: number;
  /**
   * MATERIAL / EQUIPMENT: whether the line is billed at its own price and
   * printed on the customer's document. `false` contributes nothing to what the
   * customer pays while its cost still counts toward margin. Absent reads as
   * chargeable, so every estimate saved before the flag existed bills and prints
   * unchanged.
   *
   * The CATALOG's answer, and only the default for `pricingTreatment`.
   */
  chargeable?: boolean;
  /**
   * How this document charges for the part: `included_in_service` means its
   * money is already inside the service carrying it, so its own line charges
   * zero — the double-charging rule. `separate_charge` bills it in its own
   * right. Absent derives from `chargeable`.
   */
  pricingTreatment?: PricingTreatment | null;
  /**
   * Markup percentage this line uses instead of the catalog item's own — the
   * top rung of the markup resolution order.
   */
  markupOverride?: number | null;
  /**
   * The operator has hidden this line from the customer's document. Default
   * false. Legacy spelling of the field below, kept because saved estimates
   * carry it — read both through `visibleToCustomerFlag` in line-economics
   * rather than testing either one directly.
   */
  hiddenFromCustomer?: boolean;
  /**
   * The CANONICAL, positive form: whether this line renders on the customer's
   * document. Absent means VISIBLE.
   *
   * Independent of `chargeable` in BOTH directions. Hidden and chargeable is a
   * real state — the amount stays in the subtotal, the tax and the total, the
   * customer simply never sees the row. Visible and non-chargeable is the other
   * one, and prints as "Included" with no amount.
   */
  visibleToCustomer?: boolean;
  /** NOT-605 — item-level member pricing snapshot, carried through save so the
   * preview, "✦ Member price" badge, and PDF survive a load→edit→save round trip. */
  memberPrice?: number | null;
  memberDiscountPercent?: number | null;
  /** NOT-605 — plan id of an in-cart MEMBERSHIP line (join-and-save), so a
   * reloaded estimate can re-derive the prospective membership in the preview. */
  membershipPlanId?: string | null;
  membershipSetupFee?: number;
  membershipSetupFeeFor?: string;
  membershipSetupFeePlanId?: string;
  membershipBillingOptionId?: string | null;
  /** The recipient chooses among the frozen billing options before approval. */
  membershipCustomerChoosesBillingOption?: boolean;
  currency: string;
  priceStrategy: string;
  taxable: boolean;
  /** Taxable setting copied from pricebook before any estimate-only override. */
  pricebookTaxable?: boolean;
  taxCategory?: string | null;
  taxCode?: string | null;
  taxLineType?: string | null;
  /**
   * Where this line's money posts. Snapshotted from the catalog item, or typed
   * on the line when there is no catalog item behind it. Absent means the line
   * posts to no account — which is a real state, not a default worth inventing.
   */
  glAccount?: string | null;
  glCogs?: string | null;
  glExpenseAccount?: string | null;
  glClass?: string | null;
  displayConfig?: DisplayConfig | null;
  quantity: number;
  manualPriceOverride?: number | null;
  discountAmount: number;
  discountPercent: number;
  lineSubtotal: number;
  lineTax: number;
  lineTotal: number;
  isOptional: boolean;
  isSelected: boolean;
  source: 'CATALOG' | 'AI_RECOMMENDATION' | 'MANUAL';
  taxSnapshot?: Record<string, unknown> | import('./tax').TaxSnapshot | null;
  // Per-item sales attribution (spec v1.2 §Chunk 6)
  addedBy?: string;
  addedByRole?: 'OFFICE' | 'TECH';
  isUpsell?: boolean;
  upsellContext?: string;
  sortOrder?: number;
  /**
   * Hours of work recorded against this service.
   *
   * On a DYNAMIC service they drive the price, through the formula. On a FLAT
   * service they are COST ONLY — the price stays the number the operator typed
   * and the hours move gross margin, exactly as a material's cost does. See
   * SPEC-line-item-model, "Flat vs Dynamic".
   */
  estimatedHours?: number;
  /**
   * What an hour of this service COSTS us — never what it sells for.
   *
   * `laborRate` is the billing rate and prices a dynamic service; this is the
   * other half of the pair, and it is what `document-costing` reads. Keeping
   * them apart is what lets labor earn a margin at all.
   */
  laborCostRate?: number;
  /** Frozen dynamic-service configuration; later pricebook profile edits do not alter it. */
  dynamicPricingConfig?: DocumentDynamicPricingConfig;
  imageUrl?: string | null;
  imagePreviewUrl?: string | null;
  /**
   * NOT-590 — stable identity for this line within the document, so a service
   * and the component lines it produced can reference each other. Positional
   * index was never safe for that: reordering or deleting a line would
   * silently re-parent another service's components.
   */
  lineKey?: string;
  /**
   * NOT-560 / SPEC D3 — display-only diagnostic-fee credit line (negative
   * amount) printed on an estimate spawned from a service call under the
   * CREDIT_ON_ESTIMATE policy, and carried onto the job at conversion.
   */
  isServiceCallFeeCredit?: boolean;
  /** On a component line: the `lineKey` of the SERVICE that includes it. */
  parentLineKey?: string;
  /** On a SERVICE line that has components. */
  componentDisplayMode?: ComponentDisplayMode;
  equipmentIds?: string[];
  equipmentLocationOverrideReason?: string;
  /**
   * NOT-628 — the tech performed the plan's first included visit on the same
   * trip as the sale, so the sold series starts at the next occurrence.
   */
  membershipDeliveredServiceIds?: string[];
}

export interface PdfDocument {
  id: string;
  tenantId: string;
  entityType: string;
  entityId: string;
  url: string;
  status: 'active' | 'superseded';
  reason?: string | null;
  createdAt: string;
  createdBy?: string | null;
}

export interface Estimate {
  id: string;
  wisetack?: import('./public').PublicWisetackCta | null;
  tenantId: string;
  title?: string | null;
  scopeOfWork?: string | null;
  estimateNumber: string;
  status: EstimateStatus;
  /**
   * Derived platform origin (read-only). Rows imported from an accounting
   * integration predate the taxonomy and carry their record origin instead
   * ('notifi', 'quickbooks', …), so the union stays open.
   */
  source?: EstimateSource | (string & {});
  /**
   * The technician who presented/prepared the estimate, resolved to a name.
   * Present on the INTERNAL detail (dashboard) response; the public projection
   * strips it and carries the name in the sibling `presentedBy` object instead.
   * The single-scope PDF surfaces it as the "Prepared by" party.
   */
  presentedByTechnicianName?: string | null;
  /** Existing job this estimate authorizes; null for estimate-first flows. */
  originJobId?: string | null;
  // NOT-560 — the service call this estimate was created *on* (fan-out: many
  // estimates per call), distinct from the 1:1 `originJobId` approval link.
  sourceJobId?: string | null;
  /** The job this estimate converted into (null until converted). */
  jobId?: string | null;
  /**
   * The converted job, named. `jobId` alone can only be linked to — this is
   * what lets a list say WHICH job ("JOB-000001") instead of a UUID nobody
   * recognises, without a second request per row.
   */
  jobReference?: {
    jobId?: string | null;
    jobNumber?: string | null;
    /**
     * How the two are related: `ESTIMATE_ON_JOB` is an estimate RAISED on a job
     * (its work is added to that job), which is not the same as an estimate
     * that BECAME one. Reading the number without the relationship is how a
     * source job gets reported as the job an estimate converted into.
     */
    relationship?: string | null;
    convertedAt?: string | null;
    convertedBy?: string | null;
  } | null;
  /** Explicit operational destination for approved Job-linked work. */
  fulfillmentMode?: EstimateFulfillmentMode | null;
  fulfilledAt?: string | null;
  fulfilledBy?: string | null;
  jobTypeId?: string | null;
  jobTypeName?: string | null;
  // NOT-542 — resolved Business Unit + how it was resolved. The literals are
  // the ones the server stores (`BusinessUnitSource`, apps/operations sales
  // shared): DERIVED = the resolver inferred it from the job type, SELECTED = a
  // human picked it up front on the create form, MANUAL_OVERRIDE = a dispatcher
  // reassigned an existing record. Null = no unit stamped. `JOB_TYPE_INHERIT`
  // stood here long after the server stopped writing it, which hid every
  // SELECTED-only path from the compiler.
  businessUnitId?: string | null;
  businessUnitSource?: 'DERIVED' | 'SELECTED' | 'MANUAL_OVERRIDE' | null;
  // ── Tax (Tax Policy layer, phase 4) ──────────────────────────────────────
  taxGroupId?: string | null;
  /** ZIP_MATCH | CITY_MATCH | ORG_DEFAULT | MANUAL | POLICY | EXEMPT | NONE */
  taxResolvedFrom?: string | null;
  /** Why an operator pinned taxGroupId manually (taxResolvedFrom MANUAL). */
  taxGroupOverrideReason?: string | null;
  /** Document tax summary frozen at pricing time. */
  taxSummary?: {
    totalTax?: number;
    breakdown?: unknown[];
    /** The tax rule that shaped this document's taxability, if any. */
    policy?: {
      id: string;
      name: string;
      taxableBases: string[];
      forcedTaxGroupId?: string | null;
    } | null;
  } | null;
  salespersonId?: string | null;
  poNumber?: string | null;
  customer: EstimateCustomer;
  lineItems: EstimateLineItem[];
  contactId?: string | null;
  serviceAddress?: {
    street: string;
    street2?: string | null;
    city: string;
    state: string;
    zip: string;
    // Address-level tax-exemption snapshot, frozen onto the document at
    // creation time (copied from the chosen ContactAddress). `addressId` is a
    // back-pointer for drift detection only.
    isTaxExempt?: boolean;
    taxExemptCertificate?: string | null;
    taxExemptExpiry?: string | null;
    addressId?: string;
  } | null;
  subtotal: number;
  taxTotal: number;
  discountTotal: number;
  discountValue?: number;
  promotionId?: string | null;
  membershipDiscountTotal: number;
  depositAmount: number;
  /**
   * NOT-604 — grandTotal minus any selected MEMBERSHIP line's total: the base
   * the deposit is charged against, persisted by the server's pricing engine.
   * Optional/null on rows priced before the column existed. The public quote
   * preview derives its own live value from the selected lines (see
   * lib/quote/selectedDeposit.ts) so it tracks optional-line toggles.
   */
  depositEligibleTotal?: number | null;
  grandTotal: number;
  membershipPlanSnapshot?: {
    membershipId?: string | null;
    membershipNumber?: string | null;
    planName?: string | null;
    totalDiscount?: number;
    lineDiscounts?: {
      lineItemId?: string;
      type: string;
      originalAmount: number;
      discountAmount: number;
      finalAmount: number;
      discountLabel: string | null;
    }[];
    /**
     * ST parity — every membership that priced at least one line (a document
     * can be priced across several active plans, best rate per line). The
     * public quote reads `planId` to tell a member of ANOTHER plan (offer
     * stays visible) apart from an accepted join-and-save (offer stays off).
     */
    memberships?: {
      membershipId?: string | null;
      membershipNumber?: string | null;
      planId?: string | null;
      planName?: string | null;
      savings?: number;
    }[];
    /**
     * NOT-625 — the persisted "join and save today" offer, computed server-side
     * at create/reprice for a NON-member document (see operations
     * sales/estimates/support/membership-offer.ts). When present it is the only
     * key a non-member snapshot carries — the member-pricing fields above are
     * then absent — so every reader of this object must tolerate either tenant.
     * The public quote page and the PDF render this snapshot verbatim; they
     * never recompute it.
     */
    offer?: EstimateMembershipOffer | null;
  } | null;
  validUntil?: string | null;
  notes?: string | null;
  /** Customer-ready message with Estimate Settings placeholders resolved. */
  renderedNotes?: string | null;
  internalNotes?: string | null;
  termsAndConditions?: string | null;
  /** How the terms were chosen: DEFAULT | TEMPLATE | CUSTOM. */
  termsMode?: string | null;
  termsTemplateIds?: string[] | null;
  /**
   * Estimate Template Builder — the document template this estimate renders
   * through (null = the tenant's default template). Assigned from the estimate
   * form; the dashboard PDF reads it to pick the layout. The PUBLIC quote
   * payload strips it (PUBLIC_ESTIMATE_OMIT), so it is authenticated-only.
   */
  docTemplateId?: string | null;
  signatureRequired: boolean;
  depositRequired: boolean;
  depositType: 'PERCENT' | 'FLAT' | null;
  depositValue: number;
  depositCollected: boolean;
  /**
   * A capture for the deposit the processor accepted and has not settled, big
   * enough to cover the whole deposit. Counts as paid for approval on the
   * customer's quote, and is what disables "Collect deposit" for staff.
   *
   * On the public quote payload AND on the staff list/detail. The staff side
   * is withheld from an actor under a PRICING or PAYMENT_INFO mask, where it
   * arrives `false` — absent is not evidence that nothing is clearing.
   */
  depositClearing?: boolean;
  /**
   * The rail of that pending capture. A card capture can sit pending too, so
   * ACH is named only for 'BANK'.
   */
  depositClearingRail?: 'BANK' | 'CARD' | null;
  /**
   * What is in flight, for the operator-facing figure beside the disabled CTA.
   * Staff payloads only — the customer's quote says whether, not how much.
   */
  depositClearingAmount?: number;
  /**
   * Public quote payload only: the customer's bank payment for the deposit was
   * accepted and then failed or was returned. The deposit is due again.
   */
  depositFailed?: boolean;
  /**
   * Public quote payload only: the clearing or failed deposit is a paper check
   * staff scanned, not the customer's own online bank payment.
   */
  depositByCheck?: boolean;
  depositCollectedAt?: string | null;
  depositPaymentMethod?: string | null;
  depositPaymentId?: string | null;
  depositNotes?: string | null;
  latestDepositPaymentStatus?:
    | 'PENDING'
    | 'COMPLETED'
    | 'FAILED'
    | 'REVERSED'
    | 'REFUNDED'
    | 'PARTIALLY_REFUNDED'
    | 'DISPUTED'
    | 'DISPUTE_UNDER_REVIEW'
    | 'CHARGEBACK_LOST'
    | 'CHARGEBACK_WON'
    | null;
  latestDepositPaymentId?: string | null;
  bookingType: EstimateBookingType;
  recurringConfig: EstimateRecurringConfig | null;
  customerApprovalMethod?: CustomerApprovalMethod | null;
  declineReason?: string | null;
  discountType?: 'PERCENT' | 'FLAT' | null;
  discountReason?: string | null;
  pricingMode: 'STANDARD' | 'TM_OVERRIDE';
  billingAddress?: {
    addressId?: string | null;
    street: string;
    street2?: string | null;
    city: string;
    state: string;
    zip: string;
    country?: string | null;
  } | null;
  billToId?: string | null;
  billToContact?: {
    billToId?: string | null;
    label?: string;
    contactName?: string;
    name?: string;
    email?: string | null;
  } | null;
  tags: { tagId: string; name?: string; color?: string | null }[];
  membership?: {
    id: string;
    planId: string;
    name: string;
    status: string;
  } | null;
  // NOT-227: compact payment-schedule summary on list rows (GET /estimates).
  paymentSchedule?: {
    hasSchedule: boolean;
    ruleCount: number;
    depositLabel?: string | null;
    stages?: { trigger: string; label: string; amount: number | null }[];
  } | null;
  metadata?: EstimateMetadata;
  sentAt?: string | null;
  approvedAt?: string | null;
  /** Refreshed on every customer view of the public quote (NOT-... viewed tracking). */
  lastViewedAt?: string | null;
  draftVersion: number;
  revisionNumber: number;
  previousVersions: EstimatePreviousVersion[];
  followUpDate?: string | null;
  createdBy?: string | null;
  updatedBy?: string | null;
  createdByName?: string | null;
  updatedByName?: string | null;
  createdAt: string;
  updatedAt: string;
  activePdf?: PdfDocument | null;
  /**
   * What a collected deposit actually took off the customer — sits beside
   * `estimate` on the detail response (a sibling, not a column) and is absent
   * for roles masked out of pricing. `serviceFeeAmount` is read off the capture
   * payment, so it is 0 for ACH/cash and for a fee reversed off a debit card.
   */
  depositCollectedSummary?: {
    depositAmount: number;
    serviceFeeAmount: number;
    chargedAmount: number;
    discountAmount?: number;
    coveredAmount?: number;
  } | null;
  // Present only on the detail response for repriceable (DRAFT/REVIEW)
  // estimates linked to a saved address whose CURRENT customer/address
  // tax-exemption no longer matches what the estimate was priced with.
  // `currentlyExempt` = what it would be if re-priced now; `documentExempt`
  // = what the estimate currently reflects. Re-pricing resolves the drift.
  taxExemptionDrift?: {
    hasDrift: boolean;
    currentlyExempt: boolean;
    documentExempt: boolean;
    source: 'ADDRESS' | 'CUSTOMER' | null;
  } | null;
}

export interface EstimatePreviousVersion {
  revisionNumber: number;
  snapshotAt: string;
  status: EstimateStatus;
  lineItems: EstimateLineItem[];
  customer: EstimateCustomer;
  serviceAddress: Estimate['serviceAddress'];
  subtotal: number;
  taxTotal: number;
  discountTotal: number;
  membershipDiscountTotal: number;
  depositAmount: number;
  grandTotal: number;
  validUntil: string | null;
  notes: string | null;
}

export interface EstimateListParams {
  status?: EstimateStatus;
  /**
   * Estimates for work at this location. Matched through the job server-side
   * until an estimate carries its own location.
   */
  locationId?: string;
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  customerName?: string;
  dateFrom?: string;
  dateTo?: string;
  estimateNumber?: string;
  title?: string;
  grandTotalGt?: number;
  grandTotalMin?: number;
  grandTotalLt?: number;
  grandTotalMax?: number;
  grandTotalEq?: number;
  updatedFrom?: string;
  updatedTo?: string;
  customerEmail?: string;
  customerPhone?: string;
  serviceAddress?: string;
  salespersonId?: string;
  // NOT-542: filter estimates to a single Business Unit.
  businessUnitId?: string;
  notes?: string;
  tagId?: string;
  membershipPlanId?: string;
  membershipSetupFee?: number;
  membershipSetupFeeFor?: string;
  membershipSetupFeePlanId?: string;
  membershipBillingOptionId?: string;
  hasPaymentSchedule?: boolean;
  fulfillmentMode?: EstimateFulfillmentMode;
  fulfillmentPending?: boolean;
  idIn?: string;
}

export interface EstimateListResponse {
  data: Estimate[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  hasMore: boolean;
}

export interface CreateEstimateDto {
  copyDuplicatePaymentSchedule?: boolean;
  paymentScheduleTemplate?: {
    rules: Array<{
      trigger:
        | 'ON_ESTIMATE_ACCEPTED'
        | 'ON_JOB_SCHEDULED'
        | 'ON_JOB_STARTED'
        | 'ON_MILESTONE_COMPLETE'
        | 'ON_JOB_COMPLETED'
        | 'DAYS_AFTER_COMPLETION'
        | 'FIXED_DATE';
      action: 'CREATE_DEPOSIT_INVOICE' | 'CREATE_INVOICE' | 'AUTO_CHARGE' | 'REQUEST_PAYMENT_ONLY';
      amountSpec: {
        kind: 'percent_of_job_total' | 'fixed_amount' | 'remaining_balance';
        value?: number;
        daysAfter?: number;
        fixedDate?: string;
      };
      autoCharge?: boolean;
      milestoneRef?: string;
    }>;
    previewText?: string;
  };
  duplicateFromEstimateId?: string;
  estimateNumber?: string;
  title?: string;
  /** Null clears the job type (and its inherited Business Unit) on update. */
  jobTypeId?: string | null;
  jobTypeName?: string;
  // NOT-542 — manual Business Unit override (dispatch:reassign only). Non-null
  // businessUnitId sets a MANUAL_OVERRIDE; null inherits from the job type.
  businessUnitId?: string | null;
  /**
   * The Business Unit the user picked in the CREATE FLOW (the step before the
   * form). Deliberately distinct from `businessUnitId` above: that one is a
   * dispatcher reassignment (MANUAL_OVERRIDE, permission-gated), while this is
   * an ordinary create-time choice anyone with `estimates:create` may make, and
   * it stamps source SELECTED. Keeping them apart leaves the override's
   * permission gate untouched.
   */
  selectedBusinessUnitId?: string;
  poNumber?: string;
  customer: EstimateCustomer;
  contactId?: string;
  lineItems: EstimateLineItem[];
  serviceAddress?: Estimate['serviceAddress'];
  subtotal?: number;
  taxTotal?: number;
  grandTotal?: number;
  discountValue?: number;
  validUntil?: string | null;
  scopeOfWork?: string;
  notes?: string;
  internalNotes?: string;
  termsAndConditions?: string;
  termsMode?: string | null;
  termsTemplateIds?: string[] | null;
  /** Document template to render through; null uses the tenant default. */
  docTemplateId?: string | null;
  discountTotal?: number;
  depositAmount?: number;
  promotionId?: string | null;
  depositRequired?: boolean;
  depositType?: 'PERCENT' | 'FLAT';
  depositValue?: number;
  bookingType?: EstimateBookingType;
  recurringConfig?: EstimateRecurringConfig | null;
  discountType?: 'PERCENT' | 'FLAT';
  discountReason?: string;
  pricingMode?: 'STANDARD' | 'TM_OVERRIDE';
  customerApprovalMethod?: CustomerApprovalMethod;
  signatureRequired?: boolean;
  billingAddress?: Estimate['billingAddress'];
  tagIds?: string[];
  pendingAttachmentId?: string;
  metadata?: EstimateMetadata;
  // NOT-560 — the one-off job this estimate is being created on. Set when the
  // operator started from a job's "Create estimate": the estimate is authored
  // from scratch here, and this is what keeps the job → estimate link.
  sourceJobId?: string;
  fulfillmentMode?: EstimateFulfillmentMode;
  // The visit on `sourceJobId` the estimate is quoted on, when the operator
  // started from a visit rather than the job. Names the presenter exactly
  // instead of leaving the server to guess from the earliest appointment.
  originAppointmentId?: string;
}

export interface CreateEstimateFromCatalogDto {
  customer: EstimateCustomer;
  contactId?: string;
  catalogItemIds: string[];
  serviceAddress?: Estimate['serviceAddress'];
  validUntil?: string;
  notes?: string;
  internalNotes?: string;
  depositAmount?: number;
  metadata?: EstimateMetadata;
}

export type UpdateEstimateDto = Partial<
  Pick<
    Estimate,
    | 'estimateNumber'
    | 'title'
    | 'businessUnitId'
    | 'customer'
    | 'lineItems'
    | 'contactId'
    | 'serviceAddress'
    | 'subtotal'
    | 'taxTotal'
    | 'grandTotal'
    | 'validUntil'
    | 'scopeOfWork'
    | 'notes'
    | 'internalNotes'
    | 'termsAndConditions'
    | 'termsMode'
    | 'termsTemplateIds'
    | 'docTemplateId'
    | 'signatureRequired'
    | 'discountTotal'
    | 'promotionId'
    | 'depositAmount'
    | 'depositRequired'
    | 'depositType'
    | 'depositValue'
    | 'bookingType'
    | 'recurringConfig'
    | 'discountType'
    | 'discountReason'
    | 'pricingMode'
    | 'customerApprovalMethod'
    | 'billingAddress'
    | 'metadata'
  >
> & {
  tagIds?: string[];
  /**
   * Required to save an edit to an APPROVED estimate. Confirms that the save
   * dismisses the approval: the approved document is archived as a previous
   * version, the approval and signature are cleared, and the estimate returns
   * to REVISED. Ignored in every other status.
   */
  dismissApproval?: boolean;
};

/**
 * Whether an APPROVED estimate may still be edited (dismissing its approval).
 * Mirrors `approvalDismissalBlocker` in operations: once the approval has
 * become work — a job was created from it, or its scope was applied to the job
 * it was raised on — the job owns the edit.
 */
export function isApprovalDismissible(estimate: {
  status?: EstimateStatus | string | null;
  /** The job created FROM this estimate (`Estimate.jobId`), not the job it was raised on. */
  jobId?: string | null;
  fulfilledAt?: string | null;
}): boolean {
  return estimate.status === 'APPROVED' && !estimate.jobId && !estimate.fulfilledAt;
}
